import React, { useRef } from 'react'
import { motion, useInView, useScroll, useTransform } from 'framer-motion'
import { Link } from 'react-router-dom'
import {
  ArrowRight, Check, Leaf, Sun, CloudRain, Snowflake,
} from 'lucide-react'
import { Navbar, Footer } from '../components/SharedLayout'
import MetaTags from '../components/MetaTags'
import { marketingColors as colors, marketingBrand, PAPER, marketingEase as ease } from '../lib/marketingBrand'
import { HeroEmboss, MKRule, PaperTextureOverlay, SketchBreak } from '../components/marketing/MarketingChrome'

const btnPrimary =
  'group inline-flex items-center gap-3 px-10 py-5 text-[15px] font-medium font-sans tracking-[0.06em] uppercase text-white transition-all duration-500 hover:brightness-110'
const btnSecondary =
  'group inline-flex items-center gap-3 px-10 py-5 text-[15px] font-medium font-sans tracking-[0.06em] uppercase transition-all duration-500 hover:opacity-70 border'

const IMG = 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/new-website-images/'

const FadeIn: React.FC<{
  children: React.ReactNode
  className?: string
  delay?: number
  y?: number
}> = ({ children, className, delay = 0, y = 40 }) => {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once: true, margin: '-80px' })
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease }}
      className={className}
    >
      {children}
    </motion.div>
  )
}

const Hero: React.FC = () => {
  const containerRef = useRef<HTMLElement>(null)
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start'],
  })
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0])

  return (
    <section ref={containerRef} className="relative min-h-screen overflow-hidden" style={{ backgroundColor: PAPER }}>
      <div className="absolute inset-0">
        <img
          src={`${IMG}leavesingutters.jpg`}
          alt="Seasonal home care"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0" style={{ background: `linear-gradient(105deg, ${PAPER}f0 0%, ${PAPER}e0 38%, ${PAPER}70 62%, transparent 100%)` }} />
        <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${PAPER}e6 0%, transparent 25%, transparent 78%, ${PAPER}ee 100%)` }} />
      </div>
      <HeroEmboss />

      <motion.div
        style={{ opacity }}
        className="relative z-10 w-full min-h-screen flex items-center"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 xl:px-20 w-full py-32">
          <div className="max-w-2xl">
            <motion.p
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xs sm:text-sm font-semibold tracking-[0.28em] uppercase mb-5"
              style={{ color: colors.copper }}
            >
              MyHome Refresh™
            </motion.p>
            <motion.div initial={{ opacity: 0, scaleX: 0 }} animate={{ opacity: 1, scaleX: 1 }} transition={{ duration: 0.9, delay: 0.25, ease }} className="origin-left mb-8">
              <MKRule wide />
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="font-serif"
              style={{
                fontSize: 'clamp(2.5rem, 6vw, 4.25rem)',
                lineHeight: 1.08,
                letterSpacing: '-0.02em',
                color: colors.tealDark,
              }}
            >
              Every Home Has Seasons.
              <br />
              <span className="italic" style={{ color: colors.copper }}>
                Is Yours Ready for the Next One?
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="mt-8 text-lg leading-[1.85] max-w-xl font-sans"
              style={{ color: colors.drillbitSkies }}
            >
              MyHome Refresh™ is a one-time, no-commitment service that gets
              your home current, cared for, and ready for whatever's coming. One
              visit. Real answers. Nothing left to wonder about.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.65 }}
              className="mt-10"
            >
              <Link
                to="/quiz"
                className={btnSecondary}
                style={{ borderColor: colors.plaster, color: colors.drillbitSkies }}
              >
                Discover Your Archetype
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <p className="text-sm text-grey-400 mt-2">No commitment required. Cancel anytime.</p>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </section>
  )
}

const getSeasonalBanner = () => {
  const month = new Date().getMonth()
  if (month >= 2 && month <= 4) return { season: 'Spring', message: 'Spring storms are coming. Is your home ready for what the thaw reveals?', accent: '#5B9A6F' }
  if (month >= 5 && month <= 7) return { season: 'Summer', message: 'Heat and humidity are putting pressure on your home. Get ahead of it now.', accent: '#D4943A' }
  if (month >= 8 && month <= 10) return { season: 'Fall', message: 'Winter is closer than you think. Prep your home before the first freeze.', accent: '#B85C38' }
  return { season: 'Winter', message: 'Cold weather stresses your home in ways you can\'t see. Find out where you stand.', accent: '#4A90B8' }
}

const SeasonalBanner: React.FC = () => {
  const { season, message, accent } = getSeasonalBanner()
  return (
    <div style={{ backgroundColor: accent }} className="py-4">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 text-center">
        <p className="font-sans text-sm sm:text-base font-medium text-white">
          <span className="font-semibold">{season} Alert:</span> {message}
        </p>
      </div>
    </div>
  )
}

const SeasonalProblem: React.FC = () => (
  <section className="relative py-28 md:py-36 lg:py-48 overflow-hidden" style={{ backgroundColor: PAPER }}>
    <PaperTextureOverlay opacity={0.035} />
    <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
      <div className="grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <FadeIn y={60}>
            <p
              className="text-sm tracking-[0.15em] uppercase mb-4"
              style={{ color: colors.copper }}
            >
              The Problem
            </p>
            <h2
              className="font-serif"
              style={{
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                lineHeight: 1.1,
                color: colors.nearBlack,
              }}
            >
              Every Season Brings
              <br />
              Something New
            </h2>
          </FadeIn>

          <FadeIn delay={0.15}>
            <p className="mt-8 text-gray-600 leading-relaxed">
              Your home has different needs in spring than it does in fall.
              Filters need changing, systems need checking, and small
              vulnerabilities build into bigger problems when the weather shifts.
              Most homeowners don't miss it on purpose — they just never had
              someone staying ahead of it for them.
            </p>
            <p className="mt-6 text-gray-600 leading-relaxed">
              One visit gets your home current, catches what's been missed, and
              sets you up for the season ahead with complete confidence.
            </p>
          </FadeIn>
        </div>

        <FadeIn delay={0.2}>
          <div className="rounded-sm overflow-hidden shadow-[0_20px_56px_-24px_rgba(13,49,53,0.15)] ring-1 ring-black/[0.06]">
            <img
              src={`${IMG}singlefamilyhome.jpg`}
              alt="Single family home"
              className="h-auto w-full aspect-[4/3] object-cover"
            />
          </div>
        </FadeIn>
      </div>
    </div>
  </section>
)

const essentialChecklist = [
  'Mini MyHome Health Check covering two key systems of your choice',
  'Seasonal preventive maintenance tailored to the current time of year',
  'Mini MyHome Score™ with your top three prioritized recommendations',
  'Summary report so you know exactly where your home stands',
  '$50 credit toward any future Hudson My Home™ service',
  'Completed in a single two-hour visit',
]

const deluxeChecklist = [
  'Expanded home health review covering all major systems',
  'Complete preventive maintenance across all seasonal services',
  'Full MyHome Snapshot™ with a comprehensive Home Health Score',
  'Guidance and recommendations for ongoing care',
  '$100 credit toward any future Hudson My Home™ service',
  'Completed in a single 3.5-hour visit',
]

const Packages: React.FC = () => (
  <section className="py-28 md:py-36 lg:py-48" style={{ backgroundColor: colors.cream }}>
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
      <FadeIn y={60} className="text-center max-w-2xl mx-auto mb-6">
        <p
          className="text-sm tracking-[0.15em] uppercase mb-4"
          style={{ color: colors.copper }}
        >
          Packages
        </p>
        <h2
          className="font-serif"
          style={{
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            lineHeight: 1.1,
            color: colors.nearBlack,
          }}
        >
          Immediate Value. Long Term Clarity.
        </h2>
      </FadeIn>

      <FadeIn className="text-center max-w-2xl mx-auto mb-6" delay={0.1}>
        <Link
          to="/pricing"
          className="group inline-flex items-center gap-2 text-base font-medium transition-colors"
          style={{ color: colors.drillbitSkies }}
        >
          See Pricing
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
        </Link>
      </FadeIn>

      <FadeIn className="text-center max-w-2xl mx-auto mb-16" delay={0.15}>
        <p className="text-gray-600 leading-relaxed">
          Most homeowners know their home needs attention. Few know exactly where
          to start. The MyHome Refresh™ cuts through the uncertainty so the
          essentials are handled, your home's condition is documented, and you
          have a clear path forward.
        </p>
      </FadeIn>

      <div className="grid lg:grid-cols-2 gap-8">
        <FadeIn delay={0.1}>
          <div className="bg-white/90 backdrop-blur-[2px] rounded-sm p-8 lg:p-10 h-full flex flex-col" style={{ border: `1px solid ${colors.plaster}55` }}>
            <p
              className="text-sm tracking-[0.15em] uppercase mb-2"
              style={{ color: colors.copper }}
            >
              Essential
            </p>
            <h3
              className="font-serif mb-3"
              style={{
                fontSize: 'clamp(1.5rem, 3vw, 2rem)',
                lineHeight: 1.15,
                color: colors.nearBlack,
              }}
            >
              Essential MyHome Refresh™
            </h3>
            <p className="text-gray-500 text-sm italic mb-6">
              A proactive reset and professional insight, without complexity.
            </p>
            <p className="text-gray-600 leading-relaxed mb-8">
              Your Hudson conducts a guided visual review
              of two key systems of your choice — HVAC, plumbing, electrical,
              appliances, safety, or exterior. Seasonal preventive maintenance is
              completed and tailored to your home's specific needs. At the end of
              the visit, you receive a clear summary of your home's condition,
              your Mini MyHome Score™, and prioritized recommendations for what
              to address first.
            </p>

            <ul className="space-y-4 mb-8 flex-1">
              {essentialChecklist.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-gray-700">
                  <Check
                    className="w-5 h-5 flex-shrink-0 mt-0.5"
                    style={{ color: colors.copper }}
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <Link
              to="/pricing"
              className="group inline-flex items-center gap-2 text-base font-medium transition-colors"
              style={{ color: colors.drillbitSkies }}
            >
              See Pricing
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </FadeIn>

        <FadeIn delay={0.2}>
          <div
            className="rounded-sm p-8 lg:p-10 h-full flex flex-col shadow-[0_20px_56px_-24px_rgba(13,49,53,0.25)] ring-1 ring-white/10"
            style={{ background: `linear-gradient(160deg, ${colors.tealDark}, #0B272C)` }}
          >
            <p className="text-sm tracking-[0.15em] uppercase mb-2 text-white/60">
              Deluxe
            </p>
            <h3
              className="font-serif text-white mb-3"
              style={{
                fontSize: 'clamp(1.5rem, 3vw, 2rem)',
                lineHeight: 1.15,
              }}
            >
              Deluxe MyHome Refresh™
            </h3>
            <p className="text-white/60 text-sm italic mb-6">
              A deeper review and more extensive preventive care. Nothing left to
              question.
            </p>
            <p className="text-white/70 leading-relaxed mb-4">
              Everything in the Essential Refresh, plus a detailed assessment of
              all major home systems including HVAC, plumbing, electrical,
              appliances, roofing, exterior components, and safety features.
            </p>
            <p className="text-white/70 leading-relaxed mb-8">
              All seasonal maintenance services are completed in full, including
              filter replacements and vent cleanings. At the end of the visit you
              receive a full MyHome Snapshot™ complete with a Home Health Score
              and guidance for ongoing care.
            </p>

            <ul className="space-y-4 mb-8 flex-1">
              {deluxeChecklist.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-white/80">
                  <Check
                    className="w-5 h-5 flex-shrink-0 mt-0.5"
                    style={{ color: colors.copper }}
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <Link
              to="/pricing"
              className="group inline-flex items-center gap-2 text-base font-medium text-white transition-colors"
            >
              See Pricing
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </FadeIn>
      </div>
    </div>
  </section>
)

const steps = [
  {
    num: '01',
    title: 'Choose Your Service',
    body: 'Select the Essential or Deluxe Refresh based on how much detail you want. Not sure which is right for you? We can help you decide in a quick call.',
  },
  {
    num: '02',
    title: 'We Come to You',
    body: 'Your Hudson professional arrives and gets to work. We assess your chosen systems, complete your seasonal maintenance tasks, and document everything thoroughly.',
  },
  {
    num: '03',
    title: "You're Set for the Season",
    body: "Your seasonal maintenance is complete, and you leave with a clear picture of what's in good shape and what to prioritize. Your home is set up to function at its best for the season ahead.",
  },
]

const HowItWorks: React.FC = () => (
  <section className="py-28 md:py-36 lg:py-48 bg-white">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
      <FadeIn y={60} className="text-center mb-16">
        <p
          className="text-sm tracking-[0.15em] uppercase mb-4"
          style={{ color: colors.copper }}
        >
          How It Works
        </p>
        <h2
          className="font-serif"
          style={{
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            lineHeight: 1.1,
            color: colors.nearBlack,
          }}
        >
          Three Simple Steps
        </h2>
      </FadeIn>

      <div className="grid md:grid-cols-3 gap-10 max-w-5xl mx-auto">
        {steps.map((step, i) => (
          <FadeIn key={step.num} delay={i * 0.12}>
            <div className="text-center">
              <span
                className="block font-serif text-5xl mb-4"
                style={{ color: colors.teal, opacity: 0.25 }}
              >
                {step.num}
              </span>
              <h3
                className="font-serif text-xl mb-3"
                style={{ color: colors.nearBlack }}
              >
                {step.title}
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {step.body}
              </p>
            </div>
          </FadeIn>
        ))}
      </div>
    </div>
  </section>
)

const seasonalCards = [
  {
    icon: <Leaf className="w-6 h-6" />,
    name: 'Spring',
    color: '#5B9A6F',
    body: "After a long winter, we check what the cold left behind. HVAC filters, outdoor plumbing, seals, drainage, and anything that took a hit during the freeze.",
  },
  {
    icon: <Sun className="w-6 h-6" />,
    name: 'Summer',
    color: '#D4943A',
    body: "Heat and humidity put pressure on your home's systems. We check cooling efficiency, ventilation, moisture control, and appliances running harder than usual.",
  },
  {
    icon: <CloudRain className="w-6 h-6" />,
    name: 'Fall',
    color: '#B85C38',
    body: 'The most important prep season. We get your heating system, plumbing insulation, gutters, and exterior seals ready before the cold arrives.',
  },
  {
    icon: <Snowflake className="w-6 h-6" />,
    name: 'Winter',
    color: '#4A90B8',
    body: "We check what's being stressed by the cold. Sump pumps, drafts, pipe vulnerability, and anything that could become an emergency when temperatures drop.",
  },
]

const SeasonalCards: React.FC = () => (
  <section className="py-28 md:py-36 lg:py-48" style={{ backgroundColor: colors.cream }}>
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
      <FadeIn y={60} className="text-center mb-16 max-w-2xl mx-auto">
        <p
          className="text-sm tracking-[0.15em] uppercase mb-4"
          style={{ color: colors.copper }}
        >
          Year-Round Care
        </p>
        <h2
          className="font-serif"
          style={{
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            lineHeight: 1.1,
            color: colors.nearBlack,
          }}
        >
          Every System. Every Season.
          <br />
          Always at Its Best.
        </h2>
      </FadeIn>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {seasonalCards.map((s, i) => (
          <FadeIn key={s.name} delay={i * 0.1}>
            <div className="bg-white/90 backdrop-blur-[2px] rounded-sm overflow-hidden h-full flex flex-col" style={{ border: `1px solid ${colors.plaster}55` }}>
              <div
                className="px-6 py-5 flex items-center gap-3 text-white"
                style={{ backgroundColor: s.color }}
              >
                {s.icon}
                <h3 className="font-medium text-lg">{s.name}</h3>
              </div>
              <div className="px-6 py-6 flex-1">
                <p className="text-gray-600 text-sm leading-relaxed">
                  {s.body}
                </p>
              </div>
            </div>
          </FadeIn>
        ))}
      </div>
    </div>
  </section>
)

const MembershipUpsell: React.FC = () => (
  <section className="py-28 md:py-36 lg:py-48" style={{ backgroundColor: colors.tealDark }}>
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 text-center">
      <FadeIn y={60}>
        <h2
          className="font-serif text-white"
          style={{
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            lineHeight: 1.1,
          }}
        >
          Wish You Had This Every Season?
        </h2>
      </FadeIn>

      <FadeIn delay={0.1}>
        <p className="text-white/70 mt-6 max-w-2xl mx-auto text-lg leading-relaxed">
          A Hudson My Home™ Membership means every season is handled
          automatically. No scheduling, no remembering, and no wondering what
          your home needs next.
        </p>
        <div className="mt-6 mx-auto max-w-lg px-6 py-4 border border-white/20 rounded-md" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}>
          <p className="text-white font-medium text-base">
            Your Refresh credit ($50 Essential / $100 Deluxe) applies directly toward your first month of membership.
          </p>
        </div>
      </FadeIn>

      <FadeIn delay={0.2}>
        <div className="mt-10">
          <Link
            to="/membership"
            className="group inline-flex items-center gap-3 px-10 py-5 text-[15px] font-medium font-sans tracking-[0.06em] uppercase transition-all duration-500 hover:opacity-90"
            style={{ backgroundColor: marketingBrand.white, color: colors.tealDark }}
          >
            Learn About Membership
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </FadeIn>
    </div>
  </section>
)

const MyHomeRefreshPage: React.FC = () => (
  <div className="min-h-screen" style={{ backgroundColor: marketingBrand.tapeTurquoise }}>
    <MetaTags
      title="MyHome Refresh | One-Time Home Reset | Hudson My Home"
      description="Not ready for membership? MyHome Refresh gives your home a seasonal reset with a professional inspection and actionable priorities — $299 one-time."
    />
    <Navbar />
    <main>
      <Hero />
      <SeasonalBanner />
      <SeasonalProblem />
      <SketchBreak />
      <Packages />
      <HowItWorks />
      <SeasonalCards />
      <MembershipUpsell />
    </main>
    <Footer />
  </div>
)

export default MyHomeRefreshPage
